import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import {
  fetchPlayerMatches,
  fetchPlayerOverview,
  downloadPlayerReportPdf,
} from '../../pages/player/api';
import { InlinePlayerLoader } from '../PlayerLoader';
import { useLexicon } from '../../hooks/useLexicon';
import ReportsUpgradeSheet from '../ReportsUpgradeSheet';
import {
  findFirstPlayedMatch,
  previousSeasonId as getPreviousSeasonId,
  REPORTS_PHASE,
  useReportsPhase,
} from '../../helpers/reportsPhase';

const REPORTS_ADDON_ENABLED = process.env.REACT_APP_REPORTS_ADDON_ENABLED !== 'false';

const triggerBlobDownload = (data, filename) => {
  const blob = data instanceof Blob ? data : new Blob([data], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Defer revoke so iOS Safari finishes streaming the download.
  setTimeout(() => window.URL.revokeObjectURL(url), 2000);
};

// SMRTSTATS-312 — Three-phase reports flow per Stevan (2026-06-02):
//   phase1 (trial):  "View sample report" → scoped to first played match of
//                    current season (or previous season as fallback).
//   phase2 (active, no add-on): same affordance, but click opens the
//                    ReportsUpgradeSheet directly (no server roundtrip).
//   phase3 (active + add-on): full season report (current behavior).
const ViewFullReportLink = ({ seasonId }) => {
  const l = useLexicon();
  const phase = useReportsPhase();
  const mappedPlayerId = useSelector((state) => state.user.mappedPlayerId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [sheetMode, setSheetMode] = useState(null); // 'addon' | 'bundle' | 'unavailable' | null
  const [sample, setSample] = useState(null); // { match, seasonId } | null
  const [sampleLoaded, setSampleLoaded] = useState(false);
  const [availableSeasons, setAvailableSeasons] = useState([]); // phase3 only
  const [selectedSeasonId, setSelectedSeasonId] = useState(seasonId);
  const bundleReturnHandled = useRef(false);

  // Keep the local season selection in sync with the prop on initial load.
  useEffect(() => { setSelectedSeasonId(seasonId); }, [seasonId]);

  const isSampleMode = useMemo(
    () => phase === REPORTS_PHASE.PHASE1 || phase === REPORTS_PHASE.PHASE2,
    [phase],
  );

  // Phase 3: pull the player's available seasons once so the user can pull
  // previous-season reports too (per Stevan's spec — "all available reports
  // from previous seasons"). One overview call, results cached in state.
  useEffect(() => {
    if (phase !== REPORTS_PHASE.PHASE3 || !mappedPlayerId || !seasonId) return undefined;
    let cancelled = false;
    fetchPlayerOverview(mappedPlayerId, seasonId)
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res.data?.available_seasons) ? res.data.available_seasons : [];
        setAvailableSeasons(list);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [phase, mappedPlayerId, seasonId]);

  // Sample-match resolution for phase1/phase2. One overview call per (player,
  // season); falls back to last season if current season has no played match.
  useEffect(() => {
    if (!isSampleMode || !mappedPlayerId || !seasonId) return undefined;
    let cancelled = false;
    setSampleLoaded(false);
    (async () => {
      try {
        const currentRes = await fetchPlayerOverview(mappedPlayerId, seasonId);
        const currentData = currentRes.data;
        const currentMatch = findFirstPlayedMatch(currentData?.matches_list);
        if (currentMatch) {
          if (!cancelled) {
            setSample({ match: currentMatch, seasonId });
            setSampleLoaded(true);
          }
          return;
        }
        const prevId = getPreviousSeasonId(currentData?.available_seasons, seasonId);
        if (!prevId) {
          if (!cancelled) {
            setSample(null);
            setSampleLoaded(true);
          }
          return;
        }
        const prevRes = await fetchPlayerOverview(mappedPlayerId, prevId);
        const prevMatch = findFirstPlayedMatch(prevRes.data?.matches_list);
        if (!cancelled) {
          setSample(prevMatch ? { match: prevMatch, seasonId: prevId } : null);
          setSampleLoaded(true);
        }
      } catch {
        if (!cancelled) {
          setSample(null);
          setSampleLoaded(true);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [isSampleMode, mappedPlayerId, seasonId]);

  const tryDownloadFullSeason = async () => {
    const targetSeasonId = selectedSeasonId || seasonId;
    const matchesRes = await fetchPlayerMatches({ year_season_id: targetSeasonId, per_page: 100 });
    const matches = matchesRes.data?.data
      ?? matchesRes.data?.results
      ?? (Array.isArray(matchesRes.data) ? matchesRes.data : []);
    const competitionIds = Array.from(new Set(
      matches.map((m) => m.competition_id).filter((id) => id != null),
    ));
    if (competitionIds.length === 0) {
      setError(l(1881));
      return;
    }
    const pdfRes = await downloadPlayerReportPdf(targetSeasonId, competitionIds);
    triggerBlobDownload(pdfRes.data, `player-report-season-${targetSeasonId}.pdf`);
  };

  const tryDownloadSample = async () => {
    if (!sample) {
      setError(l(1882));
      return;
    }
    const dateStr = String(sample.match.date).slice(0, 10);
    const competitionIds = sample.match.competition_id != null
      ? [sample.match.competition_id]
      : [];
    const pdfRes = await downloadPlayerReportPdf(sample.seasonId, competitionIds, {
      date_from: dateStr,
      date_to: dateStr,
    });
    triggerBlobDownload(pdfRes.data, `player-report-sample-${sample.match.id}.pdf`);
  };

  const codeToSheetMode = (code) => {
    if (!REPORTS_ADDON_ENABLED) return null;
    if (code === 'REPORT_TRIAL_EXHAUSTED') return 'bundle';
    if (code === 'REPORT_ADDON_REQUIRED') return 'addon';
    if (code === 'ADDON_NOT_SELF_SERVE') return 'unavailable';
    return null;
  };

  const onDownload = async () => {
    if (busy) return;
    // Phase 2: pre-empt the request — entitlement is known client-side.
    if (phase === REPORTS_PHASE.PHASE2) {
      setSheetMode('addon');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (phase === REPORTS_PHASE.PHASE1) {
        await tryDownloadSample();
      } else if (phase === REPORTS_PHASE.PHASE3) {
        await tryDownloadFullSeason();
      } else {
        // Locked / unknown — fall back to the existing 402-driven path so the
        // server's reason wins.
        await tryDownloadFullSeason();
      }
    } catch (e) {
      // Server returns JSON-encoded error even when the call was made with
      // responseType: 'blob'; unwrap once before showing it.
      let parsed = null;
      let message = l(1883);
      if (e?.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          parsed = JSON.parse(text);
          if (parsed?.message) message = parsed.message;
        } catch {/* keep default */}
      } else if (e?.response?.data) {
        parsed = e.response.data;
        if (parsed?.message) message = parsed.message;
      }
      const code = parsed?.code;
      const mode = codeToSheetMode(code);
      if (mode) {
        setSheetMode(mode);
      } else {
        setError(message);
      }
    } finally {
      setBusy(false);
    }
  };

  const onActivated = async () => {
    setSheetMode(null);
    await onDownload();
  };

  // Reports Add-on PRD §7.4 — post-Checkout return handling.
  useEffect(() => {
    if (!seasonId) return;
    if (bundleReturnHandled.current) return;
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('bundle') !== 'success') return;
    bundleReturnHandled.current = true;

    params.delete('bundle');
    params.delete('session_id');
    const clean = params.toString();
    const newUrl = `${window.location.pathname}${clean ? `?${clean}` : ''}`;
    window.history.replaceState({}, '', newUrl);

    const t = setTimeout(() => { onDownload(); }, 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seasonId]);

  if (!seasonId) return null;
  // Sample modes with no qualifying match (current or previous season) → hide.
  if (isSampleMode && sampleLoaded && !sample) return null;

  const label = phase === REPORTS_PHASE.PHASE3
    ? l(1878)
    : l(1879);

  // Disable until sample is resolved so we don't fire a half-formed download.
  const disabled = busy || (isSampleMode && !sampleLoaded);

  const showSeasonSwitcher = phase === REPORTS_PHASE.PHASE3 && availableSeasons.length > 1;

  return (
    <Wrap>
      {showSeasonSwitcher && (
        <SeasonSelect
          value={selectedSeasonId ?? ''}
          onChange={(e) => setSelectedSeasonId(Number(e.target.value))}
          aria-label={l(453)}
        >
          {availableSeasons.map((s) => (
            <option key={s.id} value={s.id}>{s.years ?? s.name ?? `Season ${s.id}`}</option>
          ))}
        </SeasonSelect>
      )}
      <DownloadButton type="button" onClick={onDownload} disabled={disabled}>
        {busy ? (
          <>
            <InlinePlayerLoader />
            <span>{l(1880)}</span>
          </>
        ) : (
          <span>{label}</span>
        )}
      </DownloadButton>
      {error && <ErrorText role="alert">{error}</ErrorText>}
      <ReportsUpgradeSheet
        open={sheetMode !== null}
        mode={sheetMode}
        onClose={() => setSheetMode(null)}
        onActivated={onActivated}
      />
    </Wrap>
  );
};

export default ViewFullReportLink;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DownloadButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  background: ${(props) => props.theme.row};
  color: ${(props) => props.theme.white};
  border: 1px solid ${(props) => props.theme.borderColor};
  border-radius: 8px;
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 14px;
  cursor: pointer;
  transition: border-color 120ms ease, background 120ms ease;

  &:hover:not(:disabled) {
    border-color: ${(props) => props.theme.green};
  }
  &:disabled {
    cursor: default;
    opacity: 0.85;
  }
`;

const ErrorText = styled.div`
  font-size: 12px;
  color: ${(props) => props.theme.red};
  font-family: 'SatoshiMedium', sans-serif;
`;

const SeasonSelect = styled.select`
  padding: 10px 12px;
  background: ${(props) => props.theme.row};
  color: ${(props) => props.theme.white};
  border: 1px solid ${(props) => props.theme.borderColor};
  border-radius: 8px;
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 14px;
`;
